import { AxiosError } from 'axios'
import apiClient from './api'
import {
  FlowType,
  FlowVariant,
  OnboardingProgress,
  StepFormData,
  TimeAnalytics,
  ResumePoint,
} from '../types/onboarding.types'

// Export StepFormData as StepData for backward compatibility
export type StepData = StepFormData

export class OnboardingService {
  private heartbeatInterval: number | null = null
  private readonly baseUrl = `/user-onboarding`

  constructor() {
    // No need to setup axios defaults since we use the shared apiClient
  }


  private handleError(error: unknown): Error {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError
      if (
        axiosError.response?.data &&
        typeof axiosError.response.data === 'object' &&
        'message' in axiosError.response.data
      ) {
        return new Error(String(axiosError.response.data.message))
      }
      if (axiosError.response?.status === 404) {
        return new Error(
          'No se encontró progreso de onboarding. Inicia el proceso nuevamente.'
        )
      }
      if (axiosError.response?.status === 422) {
        return new Error('Datos inválidos. Verifica la información ingresada.')
      }
    }

    if (error instanceof Error) {
      return new Error(error.message)
    }

    return new Error('Error desconocido')
  }

  // ==========================================
  // GESTIÓN DE FLUJO
  // ==========================================

  async initializeFlow(
    flowType: FlowType,
    variant: FlowVariant
  ): Promise<OnboardingProgress> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/initialize`, {
        flowType,
        variant,
      })

      // Iniciar heartbeat automático
      this.startHeartbeat()

      return response.data
    } catch (error) {
      console.error('Error initializing onboarding flow:', error)
      throw this.handleError(error)
    }
  }

  async getProgress(): Promise<OnboardingProgress> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/progress`)
      return response.data
    } catch (error) {
      console.error('Error getting progress:', error)
      throw this.handleError(error)
    }
  }

  async getResumePoint(): Promise<ResumePoint> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/resume`)
      return response.data
    } catch (error) {
      console.error('Error getting resume point:', error)
      throw this.handleError(error)
    }
  }

  // ==========================================
  // GESTIÓN DE PASOS
  // ==========================================

  async enterStep(
    stepKey: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/step/${stepKey}/enter`, {
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      })

      console.log(`✅ Entrada registrada: ${stepKey}`)
    } catch (error) {
      console.error(`❌ Error entering step ${stepKey}:`, error)
      // No lanzar error para no bloquear la navegación
    }
  }

  async exitStep(stepKey: string): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/step/${stepKey}/exit`)
      console.log(`🚪 Salida registrada: ${stepKey}`)
    } catch (error) {
      console.error(`❌ Error exiting step ${stepKey}:`, error)
      // No lanzar error para no bloquear la navegación
    }
  }

  async completeStep(
    stepKey: string,
    stepData: StepFormData,
    notes?: string
  ): Promise<void> {
    try {
      await apiClient.put(`${this.baseUrl}/step/${stepKey}/complete`, {
        stepData,
        notes,
        metadata: {
          completedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      })

      console.log(`🎉 Paso completado: ${stepKey}`)
    } catch (error) {
      console.error(`❌ Error completing step ${stepKey}:`, error)
      throw this.handleError(error)
    }
  }

  // ==========================================
  // ANALYTICS
  // ==========================================

  async getTimeAnalytics(): Promise<TimeAnalytics> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/analytics/time`)
      return response.data
    } catch (error) {
      console.error('Error getting analytics:', error)
      throw this.handleError(error)
    }
  }

  // ==========================================
  // HEARTBEAT
  // ==========================================

  startHeartbeat(): void {
    // Limpiar heartbeat existente
    this.stopHeartbeat()

    // Nuevo heartbeat cada 30 segundos
    this.heartbeatInterval = window.setInterval(async () => {
      try {
        await apiClient.post(`${this.baseUrl}/activity/heartbeat`)
      } catch (error) {
        console.warn('Heartbeat failed:', error)
        // Si falla 3 veces seguidas, pausar heartbeat
        this.stopHeartbeat()
      }
    }, 30000)
  }

  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      window.clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  // ==========================================
  // UTILIDADES
  // ==========================================

  // Método de conveniencia para verificar si el usuario necesita completar onboarding
  async needsOnboarding(): Promise<boolean> {
    try {
      const progress = await this.getProgress()
      return progress.status !== 'completed'
    } catch (error) {
      // Si hay error obteniendo progreso, asumir que no necesita onboarding
      console.warn('Error verificando estado de onboarding:', error)
      return false
    }
  }

  // Método para verificar si puede resumir onboarding
  async canResumeOnboarding(): Promise<{
    canResume: boolean
    currentStep?: string
  }> {
    try {
      const resumeData = await this.getResumePoint()
      return {
        canResume: resumeData.canResume,
        currentStep: resumeData.currentStep || undefined,
      }
    } catch (error) {
      console.warn('Error verificando reanudación:', error)
      return { canResume: false }
    }
  }

  // Cleanup al cerrar la aplicación
  cleanup(): void {
    this.stopHeartbeat()
  }
}

// Singleton instance
export const onboardingService = new OnboardingService()
